import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  UserRole,
  UserResponse,
  getAllUsersState,
  allUsersStateAction,
  UserStatus,
} from "../../../redux/features/user-slice";
import Table from "../../../components/table";
import TableBodyCell from "../../../components/table-body-cell";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import Loading from "../../../components/loading";
import Chip from "../../../components/chip";
import {
  CommonStatus,
  getTeamState,
  TeamResponse,
  teamStateAction,
} from "../../../redux/features/other-slice";
import StyledSelect from "../../../components/styled-select";
import { MultiValue, SingleValue } from "react-select";
import { OptionValue } from "../../../types/component";
import { isSelectSingleValue } from "../../../core/utils";
import paths from "../../../navigation/paths";
import { adminCat, mgTeam } from "../../../core/utils/constants";

export default function UsersPage() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const teamState = useSelector(getTeamState);
  const allUsersState = useSelector(getAllUsersState);
  const errorState = useSelector(getErrorState);

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);

  const fetchAllUsers = useCallback(async () => {
    await dispatch(
      allUsersStateAction({ teamIds: selectedTeamIds, roles: selectedRoles })
    );
  }, [dispatch, selectedTeamIds, selectedRoles]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  useEffect(() => {
    dispatch(setBreadcrumbState([{ id: "users", path: null, title: "Users" }]));
    const fetchInitialData = async () => {
      await dispatch(
        teamStateAction({
          roles: [0, 1],
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title={"All Users"} />
      <div className="flex items-center justify-end mb-4 space-x-2">
        <StyledSelect
          options={Object.values(UserRole).map((e: string) => ({
            value: e,
            label: e.capitalize(),
          }))}
          isClearable={false}
          value={{
            value: "",
            label:
              selectedRoles.length > 0
                ? `${selectedRoles.length} role${
                    selectedRoles.length > 1 ? "s" : ""
                  } selected`
                : "All roles",
          }}
          onChange={(
            value: SingleValue<OptionValue> | MultiValue<OptionValue>
          ) => {
            if (value && isSelectSingleValue(value)) {
              if (selectedRoles.includes(value.value as UserRole)) {
                setSelectedRoles(
                  selectedRoles.filter((e) => e !== value.value)
                );
              } else {
                setSelectedRoles([...selectedRoles, value.value as UserRole]);
              }
            }
          }}
          exClassName="w-48 z-20"
          exOption={(value: OptionValue) => {
            return {
              background:
                selectedRoles.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            };
          }}
        />
        <StyledSelect
          loading={teamState.loading}
          options={teamState.data.map((team: TeamResponse) => ({
            value: team.id,
            label: team.name,
          }))}
          isClearable={false}
          value={{
            value: "",
            label:
              selectedTeamIds.length > 0
                ? `${selectedTeamIds.length} team${
                    selectedTeamIds.length > 1 ? "s" : ""
                  } selected`
                : "All locations",
          }}
          onChange={(
            value: SingleValue<OptionValue> | MultiValue<OptionValue>
          ) => {
            if (value && isSelectSingleValue(value)) {
              if (selectedTeamIds.includes(value.value as string)) {
                setSelectedTeamIds(
                  selectedTeamIds.filter((e) => e !== value.value)
                );
              } else {
                setSelectedTeamIds([...selectedTeamIds, value.value as string]);
              }
            }
          }}
          exClassName="w-64 z-20"
          exOption={(value: OptionValue) => {
            return {
              background:
                selectedTeamIds.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            };
          }}
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          { id: "name", label: "Name", align: "text-left", width: "w-3/12" },
          {
            id: "position",
            label: "Position",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "customer",
            label: "Customer",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "category",
            label: "Category",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "experience",
            label: "Experience",
            align: "text-left",
            width: "w-2/12",
          },
          // {
          //   id: "role",
          //   label: "Role",
          //   align: "text-left",
          //   width: "w-1/12",
          // },
        ]}
        body={
          allUsersState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : allUsersState.data.length > 0
            ? allUsersState.data.map((user: UserResponse, index: number) => {
                return (
                  <tr
                    key={user.id}
                    className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                    onClick={() => navigate(`${paths.USERS}/${user.id}`)}
                  >
                    <TableBodyCell exClassName="font-medium">
                      <div className="flex items-center space-x-2">
                        <label className="whitespace-normal break-words hover:underline cursor-pointer">
                          {user.name}
                        </label>
                        {user.status === UserStatus.Inactive && (
                          <Chip
                            label={user.status}
                            type="error"
                            exClassName="w-fit"
                          />
                        )}
                      </div>
                      <label className="text-text-secondary text-xs font-light italic">
                        {user.email}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell exClassName="">
                      <label className="whitespace-normal break-words hover:underline cursor-pointer">
                        {user.position.name}
                      </label>
                      <br />
                      <label className="text-text-secondary text-xs font-light italic">
                        {user.team.name}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {user.customer?.name ?? "--"}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {user.category?.title ?? "--"}
                      </label>
                    </TableBodyCell>
                    <TableBodyCell>
                      <label className="whitespace-normal break-words">
                        {user.experience.title}
                      </label>
                    </TableBodyCell>
                    {/* <TableBodyCell>
                      <label className="whitespace-normal break-words capitalize">
                        {user.role}
                      </label>
                    </TableBodyCell> */}
                  </tr>
                );
              })
            : [
                <tr key="empty-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Users found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allUsers" />
    </PermissionGate>
  );
}
